import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  shippingAddress: null,
  billingAddress: null,
  sameAsBilling: true,
  deliveryMethod: 'standard', // standard, pickup, sameday
  pickupDetails: null,
  shippingRates: [],
  selectedShippingRate: null,
  deliveryGroups: {
    shipping: {
      addressGroups: {},  // Will contain address-keyed groups
      complete: false,    // Overall shipping completion
      address: null,
      rate: null
    },
    pickup: {
      locationGroups: {}, // Will contain location-keyed groups
      complete: false,    // Overall pickup completion
      location: null,
      date: null,
      time: null
    },
    sameDay: {
      addressGroups: {},  // Will contain address-keyed groups
      complete: false,    // Overall same-day completion
      address: null,
      timeSlot: null
    }
  },
  shippingComplete: false,
  paymentIntent: null,
  paymentMetadata: null,
  platformFee: 0,
  transferDetails: null,
  orderId: null,
  loading: false,
  error: null,
  unshippableItems: {
    shipping: {},  // Address ID -> [itemIds]
    pickup: {},    // Location ID -> [itemIds]
    sameDay: {},   // Address ID -> [itemIds]
    legacy: []
  },
  checkoutCurrency: 'USD',
  strapiPaymentId: null
};

// Helper functions to update overall completion
const updateShippingCompletion = (state) => {
  const addressGroups = state.deliveryGroups.shipping.addressGroups;
  const groupIds = Object.keys(addressGroups);
  
  // Complete if all address groups are complete
  state.deliveryGroups.shipping.complete = 
    groupIds.length > 0 && 
    groupIds.every(id => addressGroups[id].complete);
};

const updatePickupCompletion = (state) => {
  const locationGroups = state.deliveryGroups.pickup.locationGroups;
  const groupIds = Object.keys(locationGroups);
  
  // Complete if all location groups are complete
  state.deliveryGroups.pickup.complete = 
    groupIds.length > 0 && 
    groupIds.every(id => locationGroups[id].complete);
};

const updateSameDayCompletion = (state) => {
  const addressGroups = state.deliveryGroups.sameDay.addressGroups;
  const groupIds = Object.keys(addressGroups);
  
  // Complete if all address groups are complete
  state.deliveryGroups.sameDay.complete = 
    groupIds.length > 0 && 
    groupIds.every(id => addressGroups[id].complete);
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setShippingAddress: (state, action) => {
      state.shippingAddress = action.payload;
    },
    setBillingAddress: (state, action) => {
      state.billingAddress = action.payload;
    },
    setSameAsBilling: (state, action) => {
      state.sameAsBilling = action.payload;
    },
    setDeliveryMethod: (state, action) => {
      state.deliveryMethod = action.payload;
    },
    setPickupDetails: (state, action) => {
      state.pickupDetails = action.payload;
    },
    setShippingRates: (state, action) => {
      state.shippingRates = action.payload;
    },
    setSelectedShippingRate: (state, action) => {
      state.selectedShippingRate = action.payload;
    },
    setShippingGroupAddress: (state, action) => {
      state.deliveryGroups.shipping.address = action.payload.address;
      state.deliveryGroups.shipping.complete = 
        !!state.deliveryGroups.shipping.address && 
        !!state.deliveryGroups.shipping.rate;
    },
    setShippingGroupRate: (state, action) => {
      state.deliveryGroups.shipping.rate = action.payload.rate;
      state.deliveryGroups.shipping.complete = 
        !!state.deliveryGroups.shipping.address && 
        !!state.deliveryGroups.shipping.rate;
    },
    setPickupGroupLocation: (state, action) => {
      state.deliveryGroups.pickup.location = action.payload.location;
      state.deliveryGroups.pickup.complete = 
        !!state.deliveryGroups.pickup.location && 
        !!state.deliveryGroups.pickup.date && 
        !!state.deliveryGroups.pickup.time;
    },
    setPickupGroupDetails: (state, action) => {
      state.deliveryGroups.pickup.date = action.payload.date;
      state.deliveryGroups.pickup.time = action.payload.time;
      state.deliveryGroups.pickup.location = action.payload.location;
      state.deliveryGroups.pickup.complete = 
        !!state.deliveryGroups.pickup.location && 
        !!state.deliveryGroups.pickup.date && 
        !!state.deliveryGroups.pickup.time;
    },
    setSameDayGroupAddress: (state, action) => {
      state.deliveryGroups.sameDay.address = action.payload.address;
      state.deliveryGroups.sameDay.complete = 
        !!state.deliveryGroups.sameDay.address && 
        !!state.deliveryGroups.sameDay.timeSlot;
    },
    setSameDayGroupTimeSlot: (state, action) => {
      state.deliveryGroups.sameDay.timeSlot = action.payload.timeSlot;
      state.deliveryGroups.sameDay.complete = 
        !!state.deliveryGroups.sameDay.address && 
        !!state.deliveryGroups.sameDay.timeSlot;
    },
    setShippingComplete: (state, action) => {
      state.shippingComplete = action.payload;
    },
    setPaymentIntent: (state, action) => {
      state.paymentIntent = action.payload;
    },
    setPaymentMetadata: (state, action) => {
      state.paymentMetadata = action.payload;
    },
    setPlatformFee: (state, action) => {
      state.platformFee = action.payload;
    },
    setTransferDetails: (state, action) => {
      state.transferDetails = action.payload;
    },
    setOrderId: (state, action) => {
      state.orderId = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCheckout: () => initialState,
    removeUnshippableItems: (state, action) => {
      // Support legacy format (array of item IDs)
      if (Array.isArray(action.payload)) {
        // console.log('Using legacy format for unshippable items:', action.payload);
        if (!state.unshippableItems.legacy) {
          state.unshippableItems.legacy = [];
        }
        state.unshippableItems.legacy = [
          ...state.unshippableItems.legacy,
          ...action.payload
        ];
        return;
      }
      
      // New format with method and addressId
      const { method, addressId, items } = action.payload;
      // console.log(`Marking items as unshippable for ${method}/${addressId}:`, items);
      
      if (!state.unshippableItems[method]) {
        state.unshippableItems[method] = {};
      }
      
      // Replace the unshippable items for this method/address group
      state.unshippableItems[method][addressId] = items;
    },
    clearUnshippableItems: (state, action) => {
      // Clear unshippable items for a specific method and addressId
      const { method, addressId } = action.payload;
      // console.log(`Clearing unshippable items for ${method}/${addressId}`);
      
      if (state.unshippableItems[method] && state.unshippableItems[method][addressId]) {
        // Remove the addressId entry entirely
        delete state.unshippableItems[method][addressId];
      }
      
      // If this was the last address for this method, clean up the empty object
      if (state.unshippableItems[method] && 
          Object.keys(state.unshippableItems[method]).length === 0) {
        delete state.unshippableItems[method];
      }
    },
    setShippingAddressGroup: (state, action) => {
      const { addressId, address, items } = action.payload;
      
      // Create or update the address group
      if (!state.deliveryGroups.shipping.addressGroups[addressId]) {
        state.deliveryGroups.shipping.addressGroups[addressId] = {
          address,
          items: items.map(item => item.id || item.cartItemIndex),
          rate: null,
          complete: false
        };
      } else {
        // Update existing group
        state.deliveryGroups.shipping.addressGroups[addressId].address = address;
        state.deliveryGroups.shipping.addressGroups[addressId].items = 
          items.map(item => item.id || item.cartItemIndex);
      }
      
      // Update overall shipping completion
      updateShippingCompletion(state);
    },
    setShippingGroupRateByAddress: (state, action) => {
      const { addressId, rate } = action.payload;
      
      if (state.deliveryGroups.shipping.addressGroups[addressId]) {
        state.deliveryGroups.shipping.addressGroups[addressId].rate = rate;
        state.deliveryGroups.shipping.addressGroups[addressId].complete = true;
        
        // For backward compatibility, if this is the first/only address group,
        // also update the legacy rate
        if (Object.keys(state.deliveryGroups.shipping.addressGroups).length === 1) {
          state.deliveryGroups.shipping.rate = rate;
          state.selectedShippingRate = rate;
        }
      }
      
      // Update overall shipping completion
      updateShippingCompletion(state);
    },
    setPickupLocationGroup: (state, action) => {
      const { locationId, location, items } = action.payload;
      
      // Create or update the location group
      if (!state.deliveryGroups.pickup.locationGroups[locationId]) {
        state.deliveryGroups.pickup.locationGroups[locationId] = {
          location,
          items: items.map(item => item.id || item.cartItemIndex),
          date: null,
          time: null,
          complete: false
        };
      } else {
        // Update existing group
        state.deliveryGroups.pickup.locationGroups[locationId].location = location;
        state.deliveryGroups.pickup.locationGroups[locationId].items = 
          items.map(item => item.id || item.cartItemIndex);
      }
      
      // Update overall pickup completion
      updatePickupCompletion(state);
    },
    setPickupGroupDetailsByLocation: (state, action) => {
      const { locationId, date, time } = action.payload;
      
      if (state.deliveryGroups.pickup.locationGroups[locationId]) {
        state.deliveryGroups.pickup.locationGroups[locationId].date = date;
        state.deliveryGroups.pickup.locationGroups[locationId].time = time;
        state.deliveryGroups.pickup.locationGroups[locationId].complete = 
          !!date && !!time;
          
        // For backward compatibility
        if (Object.keys(state.deliveryGroups.pickup.locationGroups).length === 1) {
          state.deliveryGroups.pickup.date = date;
          state.deliveryGroups.pickup.time = time;
          state.pickupDetails = {
            ...state.pickupDetails,
            pickupDate: date,
            pickupTime: time
          };
        }
      }
      
      // Update overall pickup completion
      updatePickupCompletion(state);
    },
    setCheckoutCurrency: (state, action) => {
      state.checkoutCurrency = action.payload;
    },
    setStrapiPaymentId: (state, action) => {
      state.strapiPaymentId = action.payload;
    }
  }
});

export const {
  setShippingAddress,
  setBillingAddress,
  setSameAsBilling,
  setDeliveryMethod,
  setPickupDetails,
  setShippingRates,
  setSelectedShippingRate,
  setShippingGroupAddress,
  setShippingGroupRate,
  setPickupGroupLocation,
  setPickupGroupDetails,
  setSameDayGroupAddress,
  setSameDayGroupTimeSlot,
  setShippingComplete,
  setPaymentIntent,
  setStrapiPaymentId,
  setPaymentMetadata,
  setPlatformFee,
  setTransferDetails,
  setOrderId,
  setLoading,
  setError,
  clearError,
  resetCheckout,
  removeUnshippableItems,
  clearUnshippableItems,
  setShippingAddressGroup,
  setShippingGroupRateByAddress,
  setPickupLocationGroup,
  setPickupGroupDetailsByLocation,
  setCheckoutCurrency
} = checkoutSlice.actions;

// Thunk to validate address and get shipping rates
export const validateAddressAndGetRates = (address) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(clearError());
  
  try {
    // TODO: Call API to validate address
    // Placeholder for implementation
    const isValid = true;
    
    if (!isValid) {
      dispatch(setError('Invalid address. Please check and try again.'));
      dispatch(setLoading(false));
      return false;
    }
    
    // If address is valid, get shipping rates
    try {
      // TODO: Call API to get shipping rates
      // Placeholder for implementation
      const rates = [
        { id: 'rate_standard', service: 'Standard Shipping', days: '3-5', price: 5.99 },
        { id: 'rate_express', service: 'Express Shipping', days: '2-3', price: 12.99 },
        { id: 'rate_priority', service: 'Priority Shipping', days: '1-2', price: 19.99 }
      ];
      
      dispatch(setShippingRates(rates));
      dispatch(setSelectedShippingRate(rates[0]));
      dispatch(setLoading(false));
      return true;
    } catch (error) {
      dispatch(setError('Error fetching shipping rates. Please try again.'));
      dispatch(setLoading(false));
      return false;
    }
  } catch (error) {
    dispatch(setError('Error validating address. Please try again.'));
    dispatch(setLoading(false));
    return false;
  }
};

// Thunk to create payment intent
export const createPaymentIntent = (paymentData) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  dispatch(clearError());
  
  try {
    // TODO: Call API to create payment intent
    // Placeholder for implementation
    const paymentIntent = {
      clientSecret: 'mock_client_secret',
      amount: paymentData.amount
    };
    
    dispatch(setPaymentIntent(paymentIntent));
    dispatch(setLoading(false));
    return true;
  } catch (error) {
    dispatch(setError('Error creating payment. Please try again.'));
    dispatch(setLoading(false));
    return false;
  }
};

// Thunk to create order - NOW CALLS THE BACKEND API
export const createOrder = (orderData) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  dispatch(clearError());
  
  const { auth, checkout } = getState();
  const token = auth.token;
  
  // Ensure we have necessary data from checkout state
  const preparedOrderData = {
    items: orderData.items, // Already prepared in CheckoutPage?
    transactionType: orderData.transactionType,
    shippingAddress: checkout.shippingAddress?.id, // Send only the ID
    billingAddress: checkout.billingAddress?.id || checkout.shippingAddress?.id, // Send only the ID
    shippingMethod: checkout.deliveryMethod,
    shippingRate: checkout.selectedShippingRate,
    pickupDetails: checkout.deliveryMethod === 'pickup' ? checkout.pickupDetails : null,
    total: orderData.total,
    // Extract necessary payment info
    paymentMetadata: checkout.paymentMetadata, 
    // Pass calculated totals if available from frontend calculation
    subtotal: orderData.subtotal, 
    tax: orderData.tax, 
    // Add notes if available
    notes: orderData.notes || '',
  };
  
  console.log('Sending order data to backend:', preparedOrderData);

  if (!token) {
    dispatch(setError('Authentication token not found.'));
    dispatch(setLoading(false));
    return null;
  }

  // Get backend URL from environment variable
  const backendUrl = process.env.REACT_APP_STRAPI_API_URL || 'http://localhost:1337';

  try {
    const response = await fetch(`${backendUrl}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(preparedOrderData),
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error('Backend error creating order:', responseData);
      throw new Error(responseData.message || 'Failed to create order on backend');
    }

    console.log('Order created successfully via API:', responseData);
    
    // Use the actual order ID from the backend response
    const createdOrder = responseData; 
    dispatch(setOrderId(createdOrder.id)); 
    dispatch(setLoading(false));
    
    // Return the actual created order object from the backend
    return createdOrder; 
    
  } catch (error) {
    console.error('Error in createOrder thunk:', error);
    dispatch(setError(error.message || 'Error creating order. Please try again.'));
    dispatch(setLoading(false));
    return null;
  }
};

export default checkoutSlice.reducer; 